import HomeInput from "components/homePage/forms/homeInput";
import { Button } from "../button";
import styles from "./styles.module.scss";
import { Dropdown, DropdownProps, Loader } from "semantic-ui-react";
import { DropdownOption } from "dataTypes/common";
import "./inputs.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useRef } from "react";
import { ContactTopic, ContactUsRequest } from "./types";
import ErrorHandler from "components/controls/errorHandler";
import useSearchParams from "utilities/hooks/useSearchParams";
import { useHistory } from "react-router-dom";
import { useContactUsForm } from "./hooks";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";

export function ContactForm() {
  // url stuff
  const searchParams = useSearchParams();
  const history = useHistory();
  const resetUrl = () => history.replace(location.pathname);

  // form state
  const topicParam = searchParams.get("topic");
  const initialTopic = topicParam || ContactTopic.General;
  const { contactUsMutation, request, validation, validateForm, handleInputChange, resetForm } = useContactUsForm(
    new ContactUsRequest({ topic: initialTopic }),
  );

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();

      // loading
      if (contactUsMutation.isLoading) return;

      // validate
      const isValid = validateForm();
      if (!isValid) return;

      // send it
      await contactUsMutation.mutateAsync(request);
      resetForm();
      resetUrl();
      setTimeout(() => contactUsMutation.reset(), 4000);
    } catch (error) {
      ErrorHandler.HandleAxiosError(
        error as any,
        "We're sorry, but we could not submit your request at this time. Please try again later.",
      );
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  // scroll to form if topic is set in URL
  useEffect(() => {
    if (topicParam) scrollToForm();
  }, []);

  // Clear search params on unmount
  useEffect(() => {
    return () => {
      resetUrl();
    };
  }, []);

  const contactOptions: DropdownOption[] = [
    { value: ContactTopic.General, text: ContactTopic.General },
    { value: ContactTopic.Demo, text: ContactTopic.Demo },
    { value: ContactTopic.Meeting, text: ContactTopic.Meeting },
    { value: ContactTopic.UserSupport, text: ContactTopic.UserSupport },
    { value: ContactTopic.Feedback, text: ContactTopic.Feedback },
  ];

  if (contactUsMutation.isSuccess) {
    return (
      <div className={styles.success_message}>
        <FontAwesomeIcon icon={faCheckCircle} size="3x" color="green" />
        <br />
        <h3>Success!</h3>
        <p>Your message has been sent</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.root} id="public-contact-form" ref={formRef}>
      <h3>Fill out the contact form</h3>
      <div className="home_page_contact_form_inputs">
        <div className={styles.input_wrapper}>
          <HomeInput label="Name" type="text" name="name" initialValue={request.name} onChange={handleInputChange} />
          {!validation.name?.valid && <div className={styles.error_message}>Please enter name</div>}
        </div>
        <div className={styles.input_wrapper}>
          <HomeInput
            label="Email Address"
            type="text"
            name="email"
            initialValue={request.email}
            onChange={handleInputChange}
          />
          {!validation.email?.valid && <div className={styles.error_message}>Please enter valid email</div>}
        </div>
        <div className={styles.input_wrapper}>
          <Dropdown
            className="contactSection-input"
            onChange={(e: any, data: DropdownProps) => {
              if (typeof data.value === "string") handleInputChange("topic", data.value);
            }}
            options={contactOptions}
            placeholder="How can we help?"
            selectOnBlur={false}
            selection
            icon={{ className: "dropdown icon", children: <FontAwesomeIcon icon={faCaretDown} /> }}
            value={request.topic}
          />
          {!validation.topic?.valid && <div className={styles.error_message}>Please select topic</div>}
        </div>
        <div className={styles.input_wrapper}>
          <HomeInput
            label="Message"
            type="text"
            name="message"
            initialValue={request.message}
            onChange={handleInputChange}
          />
          {!validation.message?.valid && <div className={styles.error_message}>Please enter message</div>}
        </div>
      </div>
      <div className={styles.actions}>
        <small>
          <b>We care about your privacy.</b> Your data is secure and we will <br /> never share your information with
          outside parties.
        </small>
        <Button type="submit" primary radius="xl" disabled={contactUsMutation.isLoading}>
          Submit
          {contactUsMutation.isLoading && (
            <>
              ting...&nbsp; <Loader active inline size="tiny" inverted />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
